import { Card, makeStyles, Portal, useIsMobile, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { EventsMap, useClientOptions } from '@paintscout/ui-business'
import type { CalendarEvent } from 'paintscout'
import type { View } from 'react-big-calendar'
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar'
import React, { useMemo, useState } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { removeAuth0Prefix } from '@paintscout/util'
import { getCrews } from '@paintscout/util/builder'
import { getMapTitle, handleCalendarNavigate } from '../Calendar/calendarUtils'
import CalendarShim from '../Calendar/CalendarShim'
import { useProductionCalendar } from './ProductionCalendarContext'
import { ProductionCalendarToolbar } from './toolbar'
import EventsAgendaView from './EventsAgendaView'
import { DayView as DayViewComponent } from './day'
import { WeekColumnHeader, WeekEvent } from './week'
import { MonthColumnHeader, MonthDateHeader, MonthEvent } from './month'
import MobileWeekView from './week/MobileWeekView'
import MobileMonthView from './month/MobileMonthView'
import ProductionEventPopper from './ProductionEventPopper'
import ShowMore from './month/ShowMore'
import ProductionCalendarFooter from './ProductionCalendarFooter'
import { useNavContext } from 'src/components/common/Header/SideNav'

interface StyleArgs {
  view: View
  sideNavWidth: number
}

const useStyles = makeStyles<Theme, StyleArgs>((theme) => ({
  sharedStyles: {
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column',
    '& > .rbc-calendar': {
      flex: 1,
      minHeight: 0
    },
    '& .rbc-header': {
      backgroundColor: 'transparent',
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none'
    },
    '& .rbc-header + .rbc-header': {
      borderLeft: 'none'
    },
    '& .rbc-row-bg': {
      right: '0px'
    },
    '& .rbc-day-bg': {
      borderColor: theme.paintscout.palette.brand.Neutral.P90,
      '&:first-child': {
        borderLeft: 'none'
      },
      '&:last-child': {
        borderRight: 'none'
      }
    },
    '& .rbc-event': {
      backgroundColor: 'inherit',
      color: 'inherit'
    }
  },
  agendaView: {
    overflowY: 'visible',
    overflowX: 'hidden',
    boxShadow: 'none',
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    // RBC sets margin-bottom: 10px on .rbc-toolbar — drop it so the toolbar
    // (Calendar/Map + Schedule/Day/Week/Month buttons) sits flush against the
    // EventsAgendaView's Unscheduled section below.
    '& .rbc-toolbar': {
      marginBottom: 0
    }
  },
  weekViewStyles: {
    height: 'max(calc(100dvh - 300px), 800px)',
    [theme.breakpoints.down('xs')]: {
      height: 'fit-content'
    },
    '& .rbc-time-view': {
      padding: 0,
      border: 'none',
      height: '100%'
    },
    '& .rbc-time-header': {
      border: 'none',
      borderRadius: 0,
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0
    },
    '& .rbc-time-header-content': {
      border: 'none',
      flex: 1,
      minHeight: 0
    },
    '& .rbc-allday-cell': {
      flex: 1,
      minHeight: 0,
      // Scroll stacked all-day events within the strip so they don't push
      // past the ProductionCalendarFooter when many events overlap.
      overflowY: 'auto',
      scrollbarGutter: 'stable'
    },

    '& .rbc-day-bg.rbc-today, & .rbc-header.rbc-today, & .rbc-header + .rbc-today': {
      backgroundColor: 'transparent',
      color: 'inherit'
    },
    '& .rbc-time-header-cell': {
      minHeight: '32px'
    },
    '& .rbc-header': {
      padding: theme.spacing(1.125, 1.5, 1.25, 1.5)
    },
    '& .rbc-time-header-gutter': {
      display: 'none'
    },
    '& .rbc-time-content': {
      display: 'none'
    }
  },
  monthViewStyles: {
    [theme.breakpoints.down('xs')]: {
      height: 'fit-content'
    },
    '& .rbc-month-view': {
      padding: 0,
      border: 'none',
      borderRadius: 0,
      flex: 'none',
      height: 'auto'
    },
    '& .rbc-month-row': {
      // min-height (not aspectRatio) — a hard cap caused RBC's row-limit
      // measurement to under-budget space, producing "+X more" on visually
      // empty cells.
      minHeight: 'calc((100vw - 280px) / 7)',
      flex: '1 1 auto',
      flexBasis: 'auto'
    },
    '& .rbc-month-header': {
      border: 'none',
      minHeight: '28px'
    },
    '& .rbc-month-header .rbc-header': {
      padding: 0
    },
    '& .rbc-day-bg.rbc-off-range-bg': {
      background: theme.paintscout.palette.brand.Neutral.P98
    },
    '& .rbc-event-content': {
      borderRadius: theme.borderRadius.md
    }
  },
  mapCard: {
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white
  },
  map: {
    padding: theme.spacing(2),
    paddingBottom: 0,
    marginBottom: theme.spacing(2),
    overflow: 'hidden'
  },
  mapHeader: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.paintscout.palette.brand.Neutral.P98
  },
  fixedFooter: ({ sideNavWidth }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar - 1,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 -2px 4px rgba(0,0,0,0.06)',
    paddingLeft: sideNavWidth,
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }),
  fixedFooterSpacer: {
    height: 60,
    [theme.breakpoints.down('xs')]: {
      height: 110
    }
  }
}))

export default function ProductionCalendar() {
  const { events, date, setDate, view, setView, tab, loading, hideWeekends } = useProductionCalendar()
  const { sideNavWidth } = useNavContext()
  const classes = useStyles({ view, sideNavWidth })
  const localizer = momentLocalizer(moment)
  const mobile = useIsMobile({ xs: true })

  const calendarView = tab === 'map' ? 'map' : view
  const showFooter = tab === 'calendar' && (view === 'week' || view === 'month')
  const effectiveWeekView = view === 'week' && hideWeekends ? 'work_week' : calendarView

  const handleSetView = (newView: View | 'map') => {
    if (newView === 'map') {
      return
    }

    setView(newView)
  }

  return (
    <>
      <Card
        className={classNames(
          { [classes.sharedStyles]: calendarView !== 'agenda' },
          { [classes.weekViewStyles]: view === 'week' },
          { [classes.monthViewStyles]: view === 'month' },
          { [classes.agendaView]: view === 'agenda' },
          { [classes.mapCard]: calendarView === 'map' }
        )}
      >
        <BigCalendar<CalendarEvent>
          date={date}
          view={effectiveWeekView as View}
          workdaysOnly={hideWeekends}
          onView={handleSetView}
          events={events}
          localizer={localizer}
          timeslots={1}
          step={60}
          formats={{
            dateFormat: 'D',
            timeGutterFormat: 'ha',
            dayFormat: 'MMMM D, YYYY'
          }}
          scrollToTime={moment().toDate()}
          components={{
            toolbar: ProductionCalendarToolbar,
            month: { header: MonthColumnHeader, dateHeader: MonthDateHeader, event: MonthEvent },
            week: { header: WeekColumnHeader, event: WeekEvent },
            work_week: { header: WeekColumnHeader, event: WeekEvent },
            showMore: (props) => (
              <ShowMore
                {...props}
                onClick={(date: Date) => {
                  setDate(date)
                  handleSetView('day')
                }}
              />
            )
          }}
          dayLayoutAlgorithm={'no-overlap'}
          selectable={false}
          views={
            {
              day: loading ? CalendarShim : DayView,
              week: loading ? CalendarShim : mobile ? MobileWeekView : true,
              work_week: loading ? CalendarShim : mobile ? MobileWeekView : true,
              month: loading ? CalendarShim : mobile ? MobileMonthView : true,
              agenda: loading ? CalendarShim : AgendaView,
              map: loading ? CalendarShim : MapView
            } as Record<string, boolean | React.ComponentType<any>>
          }
          longPressThreshold={5}
          onSelecting={() => false}
          onDrillDown={(date: Date, view: View) => {
            setDate(date)
            handleSetView(view)
          }}
          tooltipAccessor={null}
        />
      </Card>
      {showFooter && !loading && <div className={classes.fixedFooterSpacer} aria-hidden />}
      {showFooter && !loading && (
        <Portal>
          <div className={classes.fixedFooter}>
            <ProductionCalendarFooter view={view} date={date} events={events} />
          </div>
        </Portal>
      )}
    </>
  )
}

interface EventsMapViewProps {
  events: CalendarEvent[]
  date: Date
}

function EventsMapView(props: EventsMapViewProps) {
  const { events, date } = props
  const { sideNavWidth } = useNavContext()
  const classes = useStyles({ view: 'day', sideNavWidth })
  const { selectedUsers } = useProductionCalendar()
  const { options } = useClientOptions()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)
  const livePopperEvent = selectedEvent ? events.find((ev) => ev.id === selectedEvent.id) ?? selectedEvent : null
  const crews = getCrews({ options })

  const handleMarkerClick = (event: CalendarEvent, clickEvent: React.MouseEvent) => {
    setSelectedEvent(event)
    setAnchorPosition({
      top: clickEvent.clientY,
      left: clickEvent.clientX
    })
  }

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)
      const eventIsInDateRange =
        (eventStart <= date && eventEnd >= date) ||
        eventStart.toDateString() === date.toDateString() ||
        eventEnd.toDateString() === date.toDateString()

      const crew = event.crew ? crews.find((c) => c._id === event.crew) : null
      const projectManagerId = crew?.projectManager?.id
      const userIsSelected = selectedUsers.some(
        (user) => removeAuth0Prefix(projectManagerId) === removeAuth0Prefix(user.user_id)
      )
      return eventIsInDateRange && userIsSelected
    })
  }, [events, date, selectedUsers, crews])

  return (
    <div className={classes.map}>
      <div className={classes.mapHeader}>
        <Typography variant="caption" color="textSecondary">
          Total Jobs: {filteredEvents.length}
        </Typography>
      </div>
      <EventsMap
        {...props}
        events={filteredEvents}
        onMarkerClick={(event, clickEvent) => handleMarkerClick(event, clickEvent)} // Pass the click event
        disableTooltip
      />
      {livePopperEvent && anchorPosition && (
        <ProductionEventPopper
          event={livePopperEvent}
          anchorEl={
            {
              getBoundingClientRect: () => ({
                top: anchorPosition.top,
                left: anchorPosition.left,
                right: anchorPosition.left,
                bottom: anchorPosition.top,
                width: 0,
                height: 0,
                x: anchorPosition.left,
                y: anchorPosition.top,
                toJSON: () => ({
                  top: anchorPosition.top,
                  left: anchorPosition.left,
                  right: anchorPosition.left,
                  bottom: anchorPosition.top,
                  width: 0,
                  height: 0,
                  x: anchorPosition.left,
                  y: anchorPosition.top
                })
              }),
              clientWidth: 0,
              clientHeight: 0
            } as unknown as HTMLElement
          } // Use a virtual element for positioning
          onClose={() => {
            setSelectedEvent(null)
            setAnchorPosition(null)
          }}
          closeOnCrewChange
        />
      )}
    </div>
  )
}

const AgendaView = (props) => <EventsAgendaView {...props} />

AgendaView.title = (_date: Date) => ``
AgendaView.navigate = (date: Date, action: string) => {
  let newDate = new Date(date)
  switch (action) {
    case 'PREV':
      newDate = moment(date).subtract(1, 'month').toDate()
      break
    case 'NEXT':
      newDate = moment(date).add(1, 'month').toDate()
      break
    case 'TODAY':
      newDate = new Date()
      break
    default:
      break
  }
  return newDate
}

const MapView = (props) => <EventsMapView {...props} />
MapView.title = (date: Date) => getMapTitle(date, 'day')
MapView.navigate = (date: Date, action: string) => handleCalendarNavigate(date, action, 'day')

const DayView = (props) => <DayViewComponent {...props} />
DayView.title = (date: Date) => moment(date).format('dddd, MMMM D, YYYY')
DayView.navigate = (date: Date, action: string) => handleCalendarNavigate(date, action, 'day')
